import React, { useState, useRef, useEffect } from 'react';
import './RichTextEditor.css';

const RichTextEditor = ({ value = '', onChange, placeholder = 'Start writing...' }) => {
  const [activeTab, setActiveTab] = useState('visual');
  const [content, setContent] = useState(value);
  const [htmlContent, setHtmlContent] = useState('');
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize content
  useEffect(() => {
    setContent(value);
    setHtmlContent(value);
  }, [value]);

  // Handle tab change
  const handleTabChange = (tab) => {
    if (tab === 'text') {
      setHtmlContent(content);
    } else {
      setContent(htmlContent);
    }
    setActiveTab(tab);
  };

  // Handle content changes
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    onChange(newContent);
  };

  // Handle HTML content changes
  const handleHtmlContentChange = (e) => {
    const newHtml = e.target.value;
    setHtmlContent(newHtml);
    onChange(newHtml);
  };

  // Insert image
  const insertImage = () => {
    fileInputRef.current?.click();
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('files', file);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required. Please login again.');
        return;
      }

      const response = await fetch('/api/admin/upload-content-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const imgTag = `<img src="${data.url}" alt="Uploaded image" style="max-width: 100%; height: auto;" />`;
        
        // Insert image at cursor position
        const textarea = editorRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const newText = text.substring(0, start) + imgTag + text.substring(end);
        
        setContent(newText);
        onChange(newText);
        
        // Set cursor position after image
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + imgTag.length, start + imgTag.length);
        }, 0);
      } else {
        alert('Failed to upload image. Please try again.');
      }
    } catch (error) {
      alert('Failed to upload image. Please try again.');
    }

    e.target.value = '';
  };

  return (
    <div className="custom-rich-editor">
      {/* Editor Tabs */}
      <div className="editor-tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'visual' ? 'active' : ''}`}
          onClick={() => handleTabChange('visual')}
        >
          Visual
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'text' ? 'active' : ''}`}
          onClick={() => handleTabChange('text')}
        >
          Text
        </button>
      </div>

      {/* Simple Toolbar */}
      {activeTab === 'visual' && (
        <div className="editor-toolbar">
          <div className="toolbar-group">
            <select 
              onChange={(e) => {
                const textarea = editorRef.current;
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const text = textarea.value;
                const selectedText = text.substring(start, end);
                
                let newText = '';
                if (e.target.value === 'h1') {
                  newText = text.substring(0, start) + `<h1>${selectedText}</h1>` + text.substring(end);
                } else if (e.target.value === 'h2') {
                  newText = text.substring(0, start) + `<h2>${selectedText}</h2>` + text.substring(end);
                } else if (e.target.value === 'h3') {
                  newText = text.substring(0, start) + `<h3>${selectedText}</h3>` + text.substring(end);
                } else if (e.target.value === 'bold') {
                  newText = text.substring(0, start) + `<strong>${selectedText}</strong>` + text.substring(end);
                } else if (e.target.value === 'italic') {
                  newText = text.substring(0, start) + `<em>${selectedText}</em>` + text.substring(end);
                } else if (e.target.value === 'link') {
                  const url = prompt('Enter URL:');
                  if (url) {
                    newText = text.substring(0, start) + `<a href="${url}">${selectedText}</a>` + text.substring(end);
                  } else {
                    return;
                  }
                } else if (e.target.value === 'list') {
                  newText = text.substring(0, start) + `<ul><li>${selectedText}</li></ul>` + text.substring(end);
                } else if (e.target.value === 'numbered') {
                  newText = text.substring(0, start) + `<ol><li>${selectedText}</li></ol>` + text.substring(end);
                } else if (e.target.value === 'align-left') {
                  newText = text.substring(0, start) + `<div style="text-align: left;">${selectedText}</div>` + text.substring(end);
                } else if (e.target.value === 'align-center') {
                  newText = text.substring(0, start) + `<div style="text-align: center;">${selectedText}</div>` + text.substring(end);
                } else if (e.target.value === 'align-right') {
                  newText = text.substring(0, start) + `<div style="text-align: right;">${selectedText}</div>` + text.substring(end);
                } else if (e.target.value === 'align-justify') {
                  newText = text.substring(0, start) + `<div style="text-align: justify;">${selectedText}</div>` + text.substring(end);
                }
                
                if (newText) {
                  setContent(newText);
                  onChange(newText);
                  
                  // Set cursor position after formatting
                  setTimeout(() => {
                    textarea.focus();
                    if (e.target.value === 'h1') {
                      textarea.setSelectionRange(start + 4, start + 4 + selectedText.length);
                    } else if (e.target.value === 'h2') {
                      textarea.setSelectionRange(start + 4, start + 4 + selectedText.length);
                    } else if (e.target.value === 'h3') {
                      textarea.setSelectionRange(start + 4, start + 4 + selectedText.length);
                    } else if (e.target.value === 'bold') {
                      textarea.setSelectionRange(start + 8, start + 8 + selectedText.length);
                    } else if (e.target.value === 'italic') {
                      textarea.setSelectionRange(start + 4, start + 4 + selectedText.length);
                    } else if (e.target.value === 'link') {
                      textarea.setSelectionRange(start + 9, start + 9 + selectedText.length);
                    } else if (e.target.value === 'list') {
                      textarea.setSelectionRange(start + 8, start + 8 + selectedText.length);
                    } else if (e.target.value === 'numbered') {
                      textarea.setSelectionRange(start + 8, start + 8 + selectedText.length);
                    } else if (e.target.value.startsWith('align-')) {
                      // For alignment, cursor goes inside the div
                      const divStart = newText.indexOf('<div');
                      const divEnd = newText.indexOf('>', divStart);
                      const contentStart = divEnd + 1;
                      textarea.setSelectionRange(contentStart, contentStart + selectedText.length);
                    }
                  }, 0);
                }
                
                // Reset select
                e.target.value = 'format';
              }}
              style={{
                padding: '6px 10px',
                border: '1px solid #ddd',
                borderRadius: '3px',
                fontSize: '13px',
                background: '#fff'
              }}
            >
              <option value="format">Format Text</option>
              <option value="h1">Heading 1 (#)</option>
              <option value="h2">Heading 2 (##)</option>
              <option value="h3">Heading 3 (###)</option>
              <option value="bold">Bold (**)</option>
              <option value="italic">Italic (*)</option>
              <option value="link">Link [text](url)</option>
              <option value="list">Bullet List (-)</option>
              <option value="numbered">Numbered List (1.)</option>
              <option value="align-left">Align Left</option>
              <option value="align-center">Align Center</option>
              <option value="align-right">Align Right</option>
              <option value="align-justify">Align Justify</option>
            </select>
          </div>

          <div className="toolbar-group">
            <button type="button" onClick={insertImage} className="toolbar-btn" title="Insert Image">
              🖼️ Insert Image
            </button>
          </div>

          <div className="toolbar-group">
            <span style={{ fontSize: '12px', color: '#666', padding: '6px 10px' }}>
              Select text and choose format from dropdown
            </span>
          </div>
        </div>
      )}

      {/* Editor Area */}
      {activeTab === 'visual' ? (
        <textarea
          ref={editorRef}
          className="editor-area"
          value={content}
          onChange={handleContentChange}
          placeholder={placeholder}
          style={{
            minHeight: '400px',
            padding: '20px',
            border: '1px solid #ddd',
            borderTop: 'none',
            outline: 'none',
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px',
            lineHeight: '1.6',
            background: '#fff',
            direction: 'ltr',
            textAlign: 'left',
            resize: 'vertical',
            width: '100%'
          }}
        />
      ) : (
        <textarea
          className="html-editor"
          value={htmlContent}
          onChange={handleHtmlContentChange}
          placeholder="Enter HTML content..."
          style={{
            width: '100%',
            minHeight: '400px',
            border: '1px solid #ddd',
            padding: '20px',
            fontFamily: 'Monaco, Menlo, monospace',
            fontSize: '14px',
            lineHeight: '1.5',
            background: '#fff',
            outline: 'none',
            resize: 'vertical',
            direction: 'ltr',
            textAlign: 'left'
          }}
        />
      )}

      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default RichTextEditor; 